import * as _ from 'lodash-es';
import Quill, { Delta } from 'quill/core';
import type { Range } from 'quill/core/selection';
import React, { useEffect, useMemo, useState } from 'react';
import * as styles from './FacetDropdown.module.scss';
import type { EntityFacet } from './quillFacets';
import { createSearchFacetOp, POST_STATE_FACET } from './quillFacets';
import type { postStateData } from './SearchObjects';
import { useSearchObjects } from './SearchObjects';
import useAppSelector from '../app/redux/useAppSelector';
import BorderedIcon from '../components/BorderedIcon';
import type { DropdownMenuItemType } from '../components/DropdownMenu';
import DropdownMenu from '../components/DropdownMenu';
import EmptyState from '../components/EmptyState';
import SelectEntityOption from '../components/SelectEntityOption';
import { getRangeSaver } from '../editor/RangeSaver';
import { PostType } from '../graphql/generated/graphql';
import GroupSelectOption from '../groups/GroupSelectOption';
import publicIcon from '../icons/non-prefilled/16x16/public.svg';
import { PostIconForState } from '../post/PostIcon';
import PostSelectOption from '../post/PostSelectOption';
import VerifiedIcon from '../post/VerifiedIcon';
import localEntitySearch, {
  userToEntity,
} from '../search/entitySearch/localEntitySearch';
import TopicSelectOption from '../topic/TopicSelectOption';
import UserSelectOption from '../user/UserSelectOption';
import searchHighlight from '../utils/search/searchHighlight';

const moveEntityToTop = <E extends { id: string }>(
  entity: E,
  originalEntities: E[],
): E[] => {
  const entities = [...originalEntities];
  _.remove(entities, ({ id }) => id === entity.id);
  entities.unshift(entity);
  return entities;
};

interface PostStateIconProps {
  stateId: (typeof postStateData)[number]['id'];
  focused?: boolean;
}

const PostStateIcon = ({ stateId, focused }: PostStateIconProps) => {
  switch (stateId) {
    case 'expired':
      return <VerifiedIcon bordered variant="warning" />;
    case 'public':
      return <BorderedIcon>{publicIcon}</BorderedIcon>;
    case 'verified':
      return <VerifiedIcon bordered variant="success" />;
    default:
      return (
        <PostIconForState
          state={{
            type: PostType.Post,
            draft: stateId === 'draft',
            archived: stateId === 'archived',
          }}
          prefilled={stateId === 'published'}
          bordered={focused}
        />
      );
  }
};

interface FacetDropdownProps {
  inputContainer: HTMLElement | null;
  onClose(): void;
  quill: Quill;
  facet: EntityFacet;
  range: Range;
  text: string;
}

const FacetDropdown = ({
  inputContainer,
  onClose,
  quill,
  facet,
  range,
  text,
}: FacetDropdownProps) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [facet]);

  const currentUser = useAppSelector((state) => state.session.user);
  const searchObjects = useSearchObjects();

  const entities = useMemo(() => {
    if (facet.type !== 'entity') return [];

    if (facet === POST_STATE_FACET) {
      return searchObjects.postState.objects.filter(({ name }) =>
        name.toLowerCase().includes(text.toLowerCase()),
      );
    }

    const results = localEntitySearch(
      text.trim(),
      _.pick(searchObjects, facet?.entityTypes ?? []),
      {
        limit: 25,
      },
    );

    if (facet?.prependCurrentUserOnMeQuery && text.trim() === 'me') {
      return moveEntityToTop(
        userToEntity(currentUser) as (typeof results)[number],
        results,
      );
    }

    return results;
  }, [currentUser, facet, searchObjects, text]);

  const sections = [
    {
      items: entities.map((result) => ({
        id: result.id,
        data: result,
      })),
    },
  ];

  const handleSelect = (
    item: DropdownMenuItemType<(typeof entities)[number]>,
  ) => {
    const selection = getRangeSaver(quill).getLast();

    if (selection) {
      const delta = new Delta()
        .retain(range.index)
        .delete(selection.index - range.index)
        .insert(createSearchFacetOp(facet, item.id).insert);

      quill.updateContents(delta, Quill.sources.USER);
    }
  };

  return (
    <DropdownMenu.Root
      testId="facetDropdown"
      sections={sections}
      index={index}
      inputContainer={inputContainer}
      onClose={onClose}
      onIndexChange={setIndex}
      onSelect={handleSelect}
      hotkeysEnabled
      emptyState={
        <div className={styles.emptyMessage}>
          <EmptyState
            message="No matching filters"
            imageName="notFound"
            smallText
            vertical
            noMargin
          />
        </div>
      }
      renderItem={(item: (typeof entities)[number], { focused }) => {
        switch (item.type) {
          case 'post':
            return <PostSelectOption {...item} />;
          case 'user':
            return <UserSelectOption {...item} />;
          case 'topic':
            return <TopicSelectOption {...item} />;
          case 'group':
            return <GroupSelectOption {...item} />;
          case 'postState': {
            return (
              <SelectEntityOption
                icon={<PostStateIcon stateId={item.id} focused={focused} />}
                label={searchHighlight(item.name, text)}
              />
            );
          }
          default:
            return null;
        }
      }}
    />
  );
};

export default FacetDropdown;
